import { Flex } from '@mantine/core';
import type { WithT } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';

import { type DonationForm } from '@feathr/blackbox';
import { Button, ContextMenu, type IColumn, TableColumnHeader, Text } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';
import { getIconForAction } from '@feathr/hooks';

import StatusPill from './StatusPill';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './donationFormsColumns.css';

export interface IFormPlaceholder {
  id: string;
  name: string;
  status: 'active' | 'disabled' | 'erroring' | 'archived';
  views: number;
  completions: number;
  num_donations_raised: number;
  num_tips_raised: number;
}

export function donationFormsColumns({
  t,
  localUrl,
}: WithT & { localUrl: (url: string) => string }): Array<IColumn<DonationForm>> {
  return [
    {
      id: 'name',
      Header: TableColumnHeader({
        title: t('Form name'),
        sortType: 'alpha',
      }),
      headerClassName: tableStyles.sort,
      Cell({ original }): JSX.Element {
        return (
          <Button
            className={styles.link}
            link={localUrl(original.getItemUrl('edit'))}
            type={'link'}
          >
            {original.name}
          </Button>
        );
      },
      Footer: () => <Text.DefaultMedium>Page totals</Text.DefaultMedium>,
    },
    {
      id: 'state',
      Header: TableColumnHeader({
        title: t('Status'),
        sortType: 'alpha',
      }),
      headerClassName: tableStyles.sort,
      width: 120,
      className: tableStyles.cellRight,
      Cell({ original }): JSX.Element {
        return <StatusPill status={original.get('state')} />;
      },
    },
    {
      id: 'stats__num_views',
      Header: TableColumnHeader({
        title: t('Views'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 105,
      Cell({ original }): JSX.Element {
        return <>{(original.get('stats')?.num_views ?? 0).toLocaleString()}</>;
      },
      Footer: ({ data }) => (
        <Text.DefaultMedium>
          {data
            .reduce((acc, curr) => acc + (curr._original.get('stats')?.num_views ?? 0), 0)
            .toLocaleString()}
        </Text.DefaultMedium>
      ),
    },
    {
      id: 'stats__num_submissions',
      Header: TableColumnHeader({
        title: t('Submissions'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 135,
      Cell({ original }): JSX.Element {
        return <>{original.get('stats')?.num_submissions ?? 0}</>;
      },
      Footer: ({ data }) => (
        <Text.DefaultMedium>
          {data
            .reduce((acc, curr) => acc + (curr._original.get('stats')?.num_submissions ?? 0), 0)
            .toLocaleString()}
        </Text.DefaultMedium>
      ),
    },
    {
      id: 'stats__donations_count',
      Header: TableColumnHeader({
        title: t('Donations'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 170,
      Cell({ original }): JSX.Element {
        return <>{original.get('stats')?.donations_count ?? 0}</>;
      },
      Footer: ({ data }) => (
        <Text.DefaultMedium>
          {data
            .reduce((acc, curr) => acc + (curr._original.get('stats')?.donations_count ?? 0), 0)
            .toLocaleString()}
        </Text.DefaultMedium>
      ),
    },
    {
      id: 'stats__donations_total',
      Header: TableColumnHeader({
        title: t('Total Donations'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 170,
      Cell({ original }): JSX.Element {
        const total = original.get('stats')?.donations_total ?? 0;
        return <>{numeral(total).format('$0,0.00')}</>;
      },
      Footer: ({ data }) => (
        <Text.DefaultMedium>
          {numeral(
            data
              .reduce((acc, curr) => acc + (curr._original.get('stats')?.donations_total ?? 0), 0)
              .toLocaleString(),
          ).format('$0,0.00')}
        </Text.DefaultMedium>
      ),
    },
    {
      id: 'stats__average_donation',
      Header: TableColumnHeader({
        title: t('Avg. Donation'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 170,
      Cell({ original }): JSX.Element {
        const avg = original.get('stats')?.average_donation ?? 0;
        return <>{numeral(avg).format('$0,0.00')}</>;
      },
      Footer: ({ data }) => (
        <Text.DefaultMedium>
          {numeral(
            data
              .reduce((acc, curr) => acc + (curr._original.get('stats')?.average_donation ?? 0), 0)
              .toLocaleString(),
          ).format('$0,0.00')}
        </Text.DefaultMedium>
      ),
    },
    {
      id: 'stats__growth_credits',
      Header: TableColumnHeader({
        title: t('Growth Credits'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 170,
      Cell({ original }): JSX.Element {
        const growthCredits = original.get('stats')?.growth_credits ?? 0;
        return (
          <Flex gap={8} justify={'flex-end'}>
            <Text.DefaultRegular>{growthCredits.toLocaleString()}</Text.DefaultRegular>
            <GrowthCreditIcon />
          </Flex>
        );
      },
      Footer: ({ data }) => (
        <Flex gap={8} justify={'flex-end'}>
          <Text.DefaultMedium>
            {data
              .reduce((acc, curr) => acc + (curr._original.get('stats')?.growth_credits ?? 0), 0)
              .toLocaleString()}
          </Text.DefaultMedium>
          <GrowthCreditIcon />
        </Flex>
      ),
    },
    {
      id: 'stats__credits_rate',
      Header: TableColumnHeader({
        title: t('Tip Rate'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 170,
      Cell({ original }): JSX.Element {
        const tipRate = original.get('stats')?.credits_rate ?? 0;
        return <>{numeral(tipRate).format('0.00%')}</>;
      },
    },
    {
      id: 'stats__donations_recurring_total',
      Header: TableColumnHeader({
        title: t('Recurring Donations'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 170,
      Cell({ original }): JSX.Element {
        const recurringTotal = original.get('stats')?.donations_recurring_total ?? 0;
        return <>{numeral(recurringTotal).format('$0,0.00')}</>;
      },
      Footer: ({ data }) => (
        <Text.DefaultMedium>
          {numeral(
            data
              .reduce(
                (acc, curr) => acc + (curr._original.get('stats')?.donations_recurring_total ?? 0),
                0,
              )
              .toLocaleString(),
          ).format('$0,0.00')}
        </Text.DefaultMedium>
      ),
    },
    {
      id: 'options',
      Header: TableColumnHeader({
        title: t('Options'),
      }),
      className: tableStyles.cellCenter,
      width: 80,
      Cell({ original }): JSX.Element {
        return (
          <ContextMenu buttonType={'icon'} position={'left-start'}>
            <ContextMenu.Item
              link={localUrl(original.getItemUrl('edit'))}
              prefix={getIconForAction('edit')}
            >
              {t('Edit')}
            </ContextMenu.Item>
          </ContextMenu>
        );
      },
    },
  ];
}
